/** Toast notifications, announced to screen readers through an aria-live region. */
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

export type ToastKind = 'info' | 'error' | 'success';
/** One follow-up on a toast, e.g. "ย้าย…" after "บันทึกไว้ที่: …". The toast stays longer so it can be reached. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}
interface ToastItem {
  id: number;
  message: string;
  kind: ToastKind;
  action?: ToastAction;
}

type Notify = (message: string, kind?: ToastKind, action?: ToastAction) => void;
const ToastContext = createContext<Notify>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const notify = useCallback<Notify>((message, kind = 'info', action) => {
    const id = ++seq.current;
    setItems((xs) => [...xs.filter((x) => x.message !== message).slice(-3), { id, message, kind, action }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), action ? 12_000 : kind === 'error' ? 6500 : 3500);
  }, []);
  const value = useMemo(() => notify, [notify]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="xc-toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`xc-toast xc-toast-${t.kind}`}>
            <span aria-hidden="true">{t.kind === 'error' ? '⚠' : t.kind === 'success' ? '✓' : 'ℹ'}</span>
            <span>{t.message}</span>
            {t.action && (
              <button
                type="button"
                className="xc-toast-action"
                onClick={() => {
                  setItems((xs) => xs.filter((x) => x.id !== t.id));
                  t.action!.onClick();
                }}
              >
                {t.action.label}
              </button>
            )}
            <button type="button" className="xc-toast-close" aria-label="ปิดข้อความแจ้งเตือน" onClick={() => setItems((xs) => xs.filter((x) => x.id !== t.id))}>
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
